<script setup lang="ts">

import { useStore } from "vuex";
import { computed } from "vue";

import ButtonStrip from "@/components/UI/ButtonStrip.vue";
import ButtonPrimitive from "@/components/UI/ButtonPrimitive.vue";
import MeasurementsHeightTransition from "@/components/UI/MeasurementsHeightTransition.vue";
import DrillHoleEdgeTypes from "@/components/DrillHoleEdgeTypes.vue";
import DrillHoleCustom from "@/components/DrillHoleCustom.vue";
import DrillHoleRadialSettings from "@/components/DrillHoleRadialSettings.vue";
import DrillHoleGridSettings from "@/components/DrillHoleGridSettings.vue";
import DrillHolePreset from "@/components/DrillHolePreset.vue";
import ValidationMessage from "@/components/UI/ValidationMessage.vue";
import MessageContainer from "@/components/UI/MessageContainer.vue";

const store = useStore();

const $i18n = window.measurementSelectorVariables.i18n;

const drillOption = computed(() => store.getters.drillOption );
const shape = computed(() => store.getters.shape );
const primitives = computed(() => store.getters.primitives );
const canUseExactCenter = computed(() => store.getters.canUseExactCenter );
const usesDefaultDrillHoleDimensions = computed(() => store.getters.usesDefaultDrillHoleDimensions );
const drillHoleSelectorButtons = computed(() => store.getters.drillHoleSelectorButtons );
const isCenteredDrillHoles = computed(() => [ 'none', 'custom', 'edges' ].includes( drillOption.value ) );
const activeItemIndex = computed(() => {
	const index = drillHoleSelectorButtons.value.findIndex( ( item: {
		active: boolean;
	} )=> item.active === true);

	return index === -1 ? 0 : index;
});
const showCenterOfShapeNotice = computed(() =>
	canUseExactCenter.value === false
	&& isCenteredDrillHoles.value
	&& usesDefaultDrillHoleDimensions.value === true
);
const showDrillHolesNotice = computed(() =>
	drillOption.value !== 'none'
	&& drillOption.value !== 'radial'
	&& drillOption.value !== 'grid'
	&& drillOption.value !== 'edges'
);

function updateDrillOption( option: string ) {
	store.commit("updateDrillOption", option );
}

</script>

<template>
	<button-strip :total-buttons="drillHoleSelectorButtons.length" :buttons-per-row="5" :selected-item="activeItemIndex">
		<button-primitive
			v-for="(item, index) in drillHoleSelectorButtons"
			:on-click="() => updateDrillOption(item.selection)"
			:icon="item.icon"
			:text="item.text"
			:index="index"
			:is-active="item.selection === drillOption"
		/>
	</button-strip>

	<measurements-height-transition appear mode="out-in">
		<drill-hole-edge-types v-if="drillOption === 'edges'" :shape="shape" :primitives="primitives" />
	</measurements-height-transition>

	<measurements-height-transition appear mode="out-in">
		<drill-hole-custom v-if="drillOption === 'custom'" :shape="shape" :primitives="primitives" />
	</measurements-height-transition>

	<measurements-height-transition appear mode="out-in">
		<drill-hole-radial-settings v-if="drillOption === 'radial'" />
	</measurements-height-transition>

	<measurements-height-transition appear mode="out-in">
		<drill-hole-grid-settings v-if="drillOption === 'grid'" />
	</measurements-height-transition>

	<measurements-height-transition appear mode="out-in">
		<drill-hole-preset v-if="drillOption === 'preset'" />
	</measurements-height-transition>

	<message-container>
		<validation-message
			v-if="showDrillHolesNotice"
			type="notice"
		>
			{{ $i18n.drillHolesNotice }}
		</validation-message>

		<validation-message
			v-if="showCenterOfShapeNotice"
			type="notice"
		>
			{{ $i18n.centerOfShapeNotice }}
		</validation-message>
	</message-container>
</template>
